import createSinglePartNumber from '@/api/post/company/company-price/create-single-part-number';
import {
  PrimaryButton,
  PrimaryOutlineButton,
} from '@/components/button/button-component';
import Checkbox from '@/components/react-hook-form-inputs/checkbox/checkbox-component';
import Input, {
  RawInput,
} from '@/components/react-hook-form-inputs/input/input-component';
import { APP_CONTEXT } from '@/utils/context';
import {
  requiredValidationMessage,
  requiredValidationMessageWithNumber,
} from '@/utils/validations';
import PropTypes from 'prop-types';
import { useContext } from 'react';
import { useFormContext } from 'react-hook-form';
import { BsX } from 'react-icons/bs';
import Styled from './options-part-number-style';

// PropTypes definitions for component props validation
const propTypes = {
  setModalAction: PropTypes.func,
  modalAction: PropTypes.shape({
    id: PropTypes.string,
  }),
  loading: PropTypes.bool,
  methods: PropTypes.shape({
    formState: PropTypes.object,
    register: PropTypes.func,
    control: PropTypes.func,
    watch: PropTypes.func,
  }).isRequired,
  isRaw: PropTypes.bool,
  onSubmit: PropTypes.func,
  companyId: PropTypes.string.isRequired,
  onPartNumberCreated: PropTypes.func,
};

// Default props for optional properties
const defaultProps = {
  setModalAction: () => {},
  modalAction: {},
  loading: false,
  isRaw: false,
  onSubmit: undefined,
  onPartNumberCreated: () => {},
};

/**
 * OptionsPartNumberForm component for creating part numbers within company options
 *
 * This component renders a form that allows users to create new part numbers
 * for a specific company. It includes fields for part name, part number, price,
 * and a temporary checkbox. The component handles form submission and integrates
 * with the company's pricing system.
 *
 * @param {object} props The props object.
 * @param {Function} props.setModalAction Function to control modal state and actions.
 * @param {object} props.modalAction Object containing modal action configuration.
 * @param {boolean} props.loading Global loading state for the component.
 * @param {boolean} props.isRaw Whether to use raw input components (without form context).
 * @param {Function} props.onSubmit Custom submit handler when isRaw is true.
 * @param {string} props.companyId The ID of the company for which to create the part number.
 * @param {Function} props.onPartNumberCreated Callback function called when part number is successfully created.
 * @returns {JSX.Element} The OptionsPartNumberForm component.
 */
const OptionsPartNumberForm = ({
  setModalAction,
  modalAction,
  loading,
  isRaw,
  onSubmit,
  companyId,
  onPartNumberCreated,
}) => {
  // Get selected currency from global app context
  const { selectedCurrency } = useContext(APP_CONTEXT);

  // Access form methods from React Hook Form context
  const methods = useFormContext();
  const { formState, register, handleSubmit } = methods;

  /**
   * Closes the create part number modal by resetting modal action state
   */
  const closeCreatePartNumberModal = () => {
    setModalAction({ label: '', id: null });
  };

  /**
   * Handles the creation of a new part number
   *
   * This function processes the form data, creates the part number via API,
   * and handles the response by updating the dropdown and closing the modal.
   *
   * @param {object} data Form data containing part number details
   * @param {string} data.name The name of the part
   * @param {string} data.number The part number identifier
   * @param {string|number} data.price The cost price of the part
   * @param {boolean} data.temporary Whether this is a temporary part number
   */
  const handleCreatePartNumber = async (data) => {
    // Construct part number data object for API submission
    const partNumberData = {
      name: data.name,
      part_number: data.number,
      // Ensure price is a float, default to 0 if not provided
      cost_price:
        typeof data.price === 'string'
          ? parseFloat(data.price)
          : data.price || 0,
      // Use selected currency or default to ZAR
      currency_name: selectedCurrency?.name || 'ZAR',
      company_id: companyId,
      temp_pn: data.temporary || false,
      // Set default profit margins to 0
      end_user_gross_profit: 0,
      transfer_price_gross_profit: 0,
    };

    // Submit the part number to the API
    const response = await createSinglePartNumber(partNumberData, companyId);

    // Extract the created part number data from the response
    const createdData = response.data;
    const newPartNumber = {
      id: createdData.id,
      part_number: createdData.part_number,
      name: createdData.name,
      temp_pn: createdData.temp_pn,
    };

    // Notify parent component of the newly created part number
    if (onPartNumberCreated) {
      onPartNumberCreated(newPartNumber);
    }

    // Close the modal after successful creation
    closeCreatePartNumberModal();
  };

  // Determine which input components to use based on isRaw prop
  const InputField = isRaw ? RawInput : Input;
  const isLoading = loading;

  // Styling for the temporary checkbox
  const checkboxStyle = {
    padding: '24px 0',
    fontWeight: '400',
    fontSize: '14px',
  };

  return (
    <Styled.Main>
      <Styled.FormContainer>
        {/* Temporary part number checkbox */}
        <Checkbox
          style={checkboxStyle}
          type="checkbox"
          disabled={loading}
          name="temporary"
          label="Temporary"
          defaultValue
        />

        {/* Part name input field */}
        <InputField
          disabled={isLoading}
          label="Part Name"
          placeholder="Part Name"
          name="name"
          registerOptions={{
            required: requiredValidationMessage,
          }}
          formState={formState}
          register={register}
        />

        {/* Part number input field */}
        <InputField
          disabled={isLoading}
          label="Part Number"
          placeholder="Part Number"
          name="number"
          registerOptions={{
            required: requiredValidationMessage,
          }}
          formState={formState}
          register={register}
        />

        {/* Price input field with currency symbol placeholder */}
        <InputField
          disabled={isLoading}
          label="Price"
          placeholder={selectedCurrency?.symbol || 'R'}
          name="price"
          type="number"
          registerOptions={{
            required: requiredValidationMessageWithNumber,
          }}
          formState={formState}
          register={register}
        />
      </Styled.FormContainer>

      {/* Form action buttons */}
      <Styled.ButtonContainer>
        {/* Cancel button */}
        <PrimaryOutlineButton
          buttonWidth="112px"
          margin="0 24px 0 auto"
          type="button"
          icon={<BsX />}
          loading={isLoading}
          title="Cancel"
          onClick={closeCreatePartNumberModal}
        />

        {/* Submit button - changes behavior based on isRaw prop */}
        <PrimaryButton
          buttonWidth="108px"
          type="submit"
          loading={isLoading}
          title={modalAction.id ? 'Save' : 'Add'}
          onClick={isRaw ? onSubmit : handleSubmit(handleCreatePartNumber)}
        />
      </Styled.ButtonContainer>
    </Styled.Main>
  );
};

// Apply PropTypes and defaultProps to the component
OptionsPartNumberForm.propTypes = propTypes;
OptionsPartNumberForm.defaultProps = defaultProps;

export default OptionsPartNumberForm;
